// longPressTimer — minimal helper för "hold 500ms utan rörelse" detection.
//
// Användning:
//   const timer = createLongPressTimer({ onFire: () => ... });
//   touchStart  → timer.start(startPoint);
//   touchMove   → timer.observeMove(point);   // cancellerar om dist > 8px
//   touchEnd    → timer.cancel();
//
// Pure-funktion test: kalla start, vänta ms, kolla att onFire körts.

import type { Point } from './quadrant';

const DEFAULT_DELAY_MS = 500;
const DEFAULT_MOVE_TOLERANCE = 8;

export interface LongPressTimerOptions {
  onFire: (origin: Point) => void;
  delayMs?: number;
  moveTolerance?: number;
}

export interface LongPressTimer {
  start: (origin: Point) => void;
  observeMove: (point: Point) => void;
  cancel: () => void;
  /** True när timer är aktivt och inte än fyrad. */
  isPending: () => boolean;
}

export function createLongPressTimer(opts: LongPressTimerOptions): LongPressTimer {
  const delayMs = opts.delayMs ?? DEFAULT_DELAY_MS;
  const tolerance = opts.moveTolerance ?? DEFAULT_MOVE_TOLERANCE;

  let timerId: ReturnType<typeof setTimeout> | null = null;
  let originPoint: Point | null = null;

  return {
    start(origin) {
      if (timerId !== null) clearTimeout(timerId);
      originPoint = origin;
      timerId = setTimeout(() => {
        timerId = null;
        if (originPoint) opts.onFire(originPoint);
      }, delayMs);
    },
    observeMove(point) {
      if (timerId === null || !originPoint) return;
      const dist = Math.hypot(point.x - originPoint.x, point.y - originPoint.y);
      if (dist > tolerance) {
        clearTimeout(timerId);
        timerId = null;
        originPoint = null;
      }
    },
    cancel() {
      if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
      }
      originPoint = null;
    },
    isPending() {
      return timerId !== null;
    },
  };
}

export const LONG_PRESS_CONFIG = {
  DEFAULT_DELAY_MS,
  DEFAULT_MOVE_TOLERANCE,
} as const;
